import React, { useEffect, useMemo, useState } from "react";
import PageWrapper from "../../components/PageWrapper/PageWrapper";
import { API_BASE_URL } from "../../config/apiEndpoints";

const formatDateTime = (value) => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

const normalizeBriefs = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const toDateTimeLocalValue = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const pad = (num) => String(num).padStart(2, "0");
  const year = parsed.getFullYear();
  const month = pad(parsed.getMonth() + 1);
  const day = pad(parsed.getDate());
  const hours = pad(parsed.getHours());
  const minutes = pad(parsed.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const buildDraftFromItem = (item) => ({
  venue_finalized: Boolean(item.venue_finalized),
  final_payment_amount:
    item.final_payment_amount === null || item.final_payment_amount === undefined
      ? ""
      : String(item.final_payment_amount),
  commission_amount:
    item.commission_amount === null || item.commission_amount === undefined
      ? ""
      : String(item.commission_amount),
  finalized_event_at: toDateTimeLocalValue(item.finalized_event_at),
  extra_notes: item.extra_notes || "",
  soft_deleted: Boolean(item.deleted_at),
});

const ManualBookingsAdmin = () => {
  const [bookings, setBookings] = useState([]);
  const [draftsById, setDraftsById] = useState({});
  const [savingById, setSavingById] = useState({});
  const [saveFeedbackById, setSaveFeedbackById] = useState({});
  const [showAllRecords, setShowAllRecords] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const loadBriefs = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await fetch(`${API_BASE_URL}/forms/venue-brief?limit=20`, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || `Request failed with status ${response.status}`);
        }

        const data = await response.json();
        if (!cancelled) {
          const normalized = normalizeBriefs(data);
          const nextDrafts = {};
          normalized.forEach((item, idx) => {
            const briefId = item.id || item.brief_id || `row-${idx}`;
            nextDrafts[briefId] = buildDraftFromItem(item);
          });
          setBookings(normalized);
          setDraftsById(nextDrafts);
          setSaveFeedbackById({});
        }
      } catch (fetchError) {
        if (!cancelled) {
          setError(fetchError?.message || "Failed to load manual bookings.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadBriefs();

    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  const filteredBookings = useMemo(() => {
    if (showAllRecords) return bookings;
    return bookings.filter((item) => !item.deleted_at);
  }, [bookings, showAllRecords]);

  const bookingCount = filteredBookings.length;

  const handleDraftChange = (briefId, key, value) => {
    setDraftsById((prev) => ({
      ...prev,
      [briefId]: {
        ...(prev[briefId] || {}),
        [key]: value,
      },
    }));
    setSaveFeedbackById((prev) => ({ ...prev, [briefId]: "" }));
  };

  const parseMoneyValue = (rawValue) => {
    if (rawValue === "") return null;
    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed)) return undefined;
    return parsed;
  };

  const handleSaveDraft = async (briefId) => {
    const draft = draftsById[briefId];
    if (!draft) return;

    const finalPaymentAmount = parseMoneyValue(draft.final_payment_amount);
    const commissionAmount = parseMoneyValue(draft.commission_amount);

    if (finalPaymentAmount === undefined || commissionAmount === undefined) {
      setSaveFeedbackById((prev) => ({
        ...prev,
        [briefId]: "Use valid numbers for payment and commission.",
      }));
      return;
    }

    const payload = {
      venue_finalized: Boolean(draft.venue_finalized),
      final_payment_amount: finalPaymentAmount,
      commission_amount: commissionAmount,
      finalized_event_at: draft.finalized_event_at
        ? new Date(draft.finalized_event_at).toISOString()
        : null,
      extra_notes: draft.extra_notes?.trim() ? draft.extra_notes.trim() : null,
      soft_deleted: Boolean(draft.soft_deleted),
    };

    try {
      setSavingById((prev) => ({ ...prev, [briefId]: true }));
      setSaveFeedbackById((prev) => ({ ...prev, [briefId]: "" }));

      const response = await fetch(`${API_BASE_URL}/forms/venue-brief/${briefId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Update failed with status ${response.status}`);
      }

      setBookings((prev) =>
        prev.map((item) => {
          const itemId = item.id || item.brief_id;
          if (itemId !== briefId) return item;
          return {
            ...item,
            ...payload,
            deleted_at: payload.soft_deleted
              ? item.deleted_at || new Date().toISOString()
              : null,
            updated_at: new Date().toISOString(),
          };
        })
      );
      setSaveFeedbackById((prev) => ({ ...prev, [briefId]: "Saved" }));
    } catch (saveError) {
      setSaveFeedbackById((prev) => ({
        ...prev,
        [briefId]: saveError?.message || "Failed to save changes.",
      }));
    } finally {
      setSavingById((prev) => ({ ...prev, [briefId]: false }));
    }
  };

  return (
    <PageWrapper useNewFooter useLanding2Header>
      <main className="bg-white">
        <div className="mx-auto w-full max-w-[96rem] px-6 py-10 sm:px-8">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-gray-500">
                Admin
              </p>
              <h1 className="font-heading mt-2 text-3xl font-bold text-gray-900">
                Manual bookings
              </h1>
              {/*<p className="mt-2 text-sm text-gray-500">*/}
              {/*  Showing latest venue briefs from <code>/forms/venue-brief</code>.*/}
              {/*</p>*/}
            </div>
            <button
              type="button"
              onClick={() => setRefreshTick((prev) => prev + 1)}
              className="rounded-lg bg-[#046242] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#035238] focus:outline-none focus:ring-2 focus:ring-[#046242]/40 focus:ring-offset-2"
            >
              Refresh
            </button>
          </div>

          {loading ? <p className="text-sm text-gray-600">Loading manual bookings...</p> : null}
          {error ? <p className="text-sm text-red-600">{error}</p> : null}

          {!loading && !error ? (
            <>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-gray-600">
                  {bookingCount} result{bookingCount === 1 ? "" : "s"} shown
                  {showAllRecords ? "" : ` (${bookings.length} total)`}
                </p>
                <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
                  <button
                    type="button"
                    onClick={() => setShowAllRecords(false)}
                    className={`rounded-md px-3 py-1.5 text-sm ${
                      !showAllRecords
                        ? "bg-[#046242] text-white"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    Active only
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAllRecords(true)}
                    className={`rounded-md px-3 py-1.5 text-sm ${
                      showAllRecords
                        ? "bg-[#046242] text-white"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    All records
                  </button>
                </div>
              </div>
              {bookingCount === 0 ? (
                <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-6 text-sm text-gray-600">
                  {showAllRecords
                    ? "No manual bookings found."
                    : "No active manual bookings found. Switch to All records to view archived items."}
                </div>
              ) : (
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Contact
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Event
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Date / Guests
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Area / Budget
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Submitted
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Admin fields
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {filteredBookings.map((item, idx) => {
                          const key = item.id || item.brief_id || item.created_at || idx;
                          const briefId = item.id || item.brief_id || "";
                          const draft = draftsById[key] || buildDraftFromItem(item);
                          const isSaving = Boolean(savingById[key]);
                          const feedback = saveFeedbackById[key];
                          return (
                            <tr key={key} className="align-top">
                              <td className="px-4 py-3 text-sm text-gray-700">
                                <div className="font-medium text-gray-900">{item.contact_name || "—"}</div>
                                <div>{item.contact_email || "—"}</div>
                                <div>{item.contact_phone || "—"}</div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700">
                                <div className="font-medium text-gray-900">{item.event_type || "—"}</div>
                                <div>{item.booking_frequency || "—"}</div>
                                <div className="mt-1 line-clamp-2 text-gray-600">
                                  {item.event_description || "—"}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700">
                                <div>{item.first_date || "—"}</div>
                                <div>{item.number_of_guests ? `${item.number_of_guests} guests` : "—"}</div>
                                <div>{item.duration_needed || "—"}</div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700">
                                <div>{item.target_area || "—"}</div>
                                <div>{item.budget_range || "—"}</div>
                                <div>{item.payment_preference || "—"}</div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700">
                                <div>{formatDateTime(item.created_at || item.submitted_at)}</div>
                                <div className="mt-1 text-xs text-gray-500">
                                  Updated: {formatDateTime(item.updated_at)}
                                </div>
                                <div className="mt-1 text-xs text-gray-500">
                                  Archived: {item.deleted_at ? formatDateTime(item.deleted_at) : "No"}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700 min-w-[22rem]">
                                <label className="mb-2 flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(draft.venue_finalized)}
                                    onChange={(e) =>
                                      handleDraftChange(key, "venue_finalized", e.target.checked)
                                    }
                                  />
                                  <span>Venue finalized</span>
                                </label>
                                <div className="mb-2 grid grid-cols-2 gap-2">
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={draft.final_payment_amount}
                                    onChange={(e) =>
                                      handleDraftChange(key, "final_payment_amount", e.target.value)
                                    }
                                    placeholder="Final payment"
                                    className="w-full rounded border border-gray-200 px-2 py-1"
                                  />
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={draft.commission_amount}
                                    onChange={(e) =>
                                      handleDraftChange(key, "commission_amount", e.target.value)
                                    }
                                    placeholder="Commission"
                                    className="w-full rounded border border-gray-200 px-2 py-1"
                                  />
                                </div>
                                <input
                                  type="datetime-local"
                                  value={draft.finalized_event_at}
                                  onChange={(e) =>
                                    handleDraftChange(key, "finalized_event_at", e.target.value)
                                  }
                                  className="mb-2 w-full rounded border border-gray-200 px-2 py-1"
                                />
                                <textarea
                                  rows={3}
                                  value={draft.extra_notes}
                                  onChange={(e) =>
                                    handleDraftChange(key, "extra_notes", e.target.value)
                                  }
                                  placeholder="Admin notes"
                                  className="w-full rounded border border-gray-200 px-2 py-1"
                                />
                                <label className="mt-2 flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(draft.soft_deleted)}
                                    onChange={(e) =>
                                      handleDraftChange(key, "soft_deleted", e.target.checked)
                                    }
                                  />
                                  <span>Archived (soft deleted)</span>
                                </label>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700 min-w-[12rem]">
                                <button
                                  type="button"
                                  disabled={!briefId || isSaving}
                                  onClick={() => handleSaveDraft(briefId)}
                                  className="rounded bg-[#046242] px-3 py-1.5 text-white disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  {isSaving ? "Saving..." : "Save"}
                                </button>
                                {feedback ? (
                                  <p
                                    className={`mt-2 text-xs ${
                                      feedback === "Saved" ? "text-green-700" : "text-red-600"
                                    }`}
                                  >
                                    {feedback}
                                  </p>
                                ) : null}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>
      </main>
    </PageWrapper>
  );
};

export default ManualBookingsAdmin;
